// @ts-nocheck
// this file was patched by @terra

import { z } from 'zod';
import type { JsonValueType } from '../inputTypeSchemas/JsonValueSchema';
import { AiApplicationConfigSchema } from './AiApplicationConfigSchema'
import type { AiApplicationConfig } from './AiApplicationConfigSchema'
import { DomainConfigSchema } from './DomainConfigSchema'
import type { DomainConfig } from './DomainConfigSchema'
import { TwingateNetworkWithRelationsSchema } from './TwingateNetworkRelationsSchema'
import type { TwingateNetworkWithRelations } from './TwingateNetworkRelationsSchema'
import { TailscaleNetworkWithRelationsSchema } from './TailscaleNetworkRelationsSchema'
import type { TailscaleNetworkWithRelations } from './TailscaleNetworkRelationsSchema'
import { CrawlerPeriodicSettingsSchema } from './CrawlerPeriodicSettingsSchema'
import type { CrawlerPeriodicSettings } from './CrawlerPeriodicSettingsSchema'
import { ScheduledReportRuleSchema } from './ScheduledReportRuleSchema'
import type { ScheduledReportRule } from './ScheduledReportRuleSchema'
import { DynamicApplicationScanRunWithRelationsSchema } from './DynamicApplicationScanRunRelationsSchema'
import type { DynamicApplicationScanRunWithRelations } from './DynamicApplicationScanRunRelationsSchema'
import { AutomationScheduleSchema } from './AutomationScheduleSchema'
import type { AutomationSchedule } from './AutomationScheduleSchema'
import { BrowserConfigSchema } from './BrowserConfigSchema'
import type { BrowserConfig } from './BrowserConfigSchema'
import { DiscoveryContentTypeConfigSchema } from './DiscoveryContentTypeConfigSchema'
import type { DiscoveryContentTypeConfig } from './DiscoveryContentTypeConfigSchema'
import { KeyValueSchema } from './KeyValueSchema'
import type { KeyValue } from './KeyValueSchema'
import { ApplicationLoginBehaviorSchema } from './ApplicationLoginBehaviorSchema'
import type { ApplicationLoginBehavior } from './ApplicationLoginBehaviorSchema'
import { CvssEnvironmentalRequirementsSchema } from './CvssEnvironmentalRequirementsSchema'
import type { CvssEnvironmentalRequirements } from './CvssEnvironmentalRequirementsSchema'
import { ApplicationBusinessUnitWithRelationsSchema } from './ApplicationBusinessUnitRelationsSchema'
import type { ApplicationBusinessUnitWithRelations } from './ApplicationBusinessUnitRelationsSchema'
import { ApplicationFileWithRelationsSchema } from './ApplicationFileRelationsSchema'
import type { ApplicationFileWithRelations } from './ApplicationFileRelationsSchema'
import { OpenApiWithRelationsSchema } from './OpenApiRelationsSchema'
import type { OpenApiWithRelations } from './OpenApiRelationsSchema'
import { ApplicationScopingWithRelationsSchema } from './ApplicationScopingRelationsSchema'
import type { ApplicationScopingWithRelations } from './ApplicationScopingRelationsSchema'
import { ScopingFromRepoWithRelationsSchema } from './ScopingFromRepoRelationsSchema'
import type { ScopingFromRepoWithRelations } from './ScopingFromRepoRelationsSchema'
import { AiComponentWithRelationsSchema } from './AiComponentRelationsSchema'
import type { AiComponentWithRelations } from './AiComponentRelationsSchema'
import { AiComponentRelationWithRelationsSchema } from './AiComponentRelationRelationsSchema'
import type { AiComponentRelationWithRelations } from './AiComponentRelationRelationsSchema'
import { AdaptiveScanningBlockObservationWithRelationsSchema } from './AdaptiveScanningBlockObservationRelationsSchema'
import type { AdaptiveScanningBlockObservationWithRelations } from './AdaptiveScanningBlockObservationRelationsSchema'
import { ApplicationGenerationLimitWithRelationsSchema } from './ApplicationGenerationLimitRelationsSchema'
import type { ApplicationGenerationLimitWithRelations } from './ApplicationGenerationLimitRelationsSchema'
import { FullRescanRunWithRelationsSchema } from './FullRescanRunRelationsSchema'
import type { FullRescanRunWithRelations } from './FullRescanRunRelationsSchema'
import { ApplicationSchema } from './ApplicationSchema';

/////////////////////////////////////////
// APPLICATION RELATION SCHEMA
/////////////////////////////////////////

export type ApplicationRelations = {
  kindConfig?: AiApplicationConfig | null;
  domains: DomainConfig[];
  twingateNetwork?: TwingateNetworkWithRelations | null;
  tailscaleNetwork?: TailscaleNetworkWithRelations | null;
  crawlerPeriodic?: CrawlerPeriodicSettings | null;
  scheduledReportRules: ScheduledReportRule[];
  dynamicApplicationScanScheduleRule?: ScheduledReportRule | null;
  dynamicApplicationScanRuns: DynamicApplicationScanRunWithRelations[];
  automationSchedule?: AutomationSchedule | null;
  browserConfig?: BrowserConfig | null;
  discoveryContentTypes: DiscoveryContentTypeConfig[];
  globalHeaders: KeyValue[];
  loginBehavior?: ApplicationLoginBehavior | null;
  cvssEnvironmentalRequirements?: CvssEnvironmentalRequirements | null;
  businessUnits: ApplicationBusinessUnitWithRelations[];
  files: ApplicationFileWithRelations[];
  openApis: OpenApiWithRelations[];
  applicationScoping: ApplicationScopingWithRelations[];
  scopingFromRepo: ScopingFromRepoWithRelations[];
  aiComponents: AiComponentWithRelations[];
  aiComponentRelations: AiComponentRelationWithRelations[];
  adaptiveScanningBlockObservations: AdaptiveScanningBlockObservationWithRelations[];
  generationLimits: ApplicationGenerationLimitWithRelations[];
  fullRescanRuns: FullRescanRunWithRelations[];
};

export type ApplicationWithRelations = Omit<z.infer<typeof ApplicationSchema>, "signalImportHostAliases" | "signalImportMountMappings"> & {
  signalImportHostAliases?: JsonValueType | null;
  signalImportMountMappings?: JsonValueType | null;
} & ApplicationRelations

export const ApplicationWithRelationsSchema: z.ZodType<ApplicationWithRelations> = ApplicationSchema.merge(z.object({
  kindConfig: z.lazy(() => AiApplicationConfigSchema).nullable(),
  domains: z.lazy(() => DomainConfigSchema).array(),
  twingateNetwork: z.lazy(() => TwingateNetworkWithRelationsSchema).nullable(),
  tailscaleNetwork: z.lazy(() => TailscaleNetworkWithRelationsSchema).nullable(),
  crawlerPeriodic: z.lazy(() => CrawlerPeriodicSettingsSchema).nullable(),
  scheduledReportRules: z.lazy(() => ScheduledReportRuleSchema).array(),
  dynamicApplicationScanScheduleRule: z.lazy(() => ScheduledReportRuleSchema).nullable(),
  dynamicApplicationScanRuns: z.lazy(() => DynamicApplicationScanRunWithRelationsSchema).array(),
  automationSchedule: z.lazy(() => AutomationScheduleSchema).nullable(),
  browserConfig: z.lazy(() => BrowserConfigSchema).nullable(),
  discoveryContentTypes: z.lazy(() => DiscoveryContentTypeConfigSchema).array(),
  globalHeaders: z.lazy(() => KeyValueSchema).array(),
  loginBehavior: z.lazy(() => ApplicationLoginBehaviorSchema).nullable(),
  cvssEnvironmentalRequirements: z.lazy(() => CvssEnvironmentalRequirementsSchema).nullable(),
  businessUnits: z.lazy(() => ApplicationBusinessUnitWithRelationsSchema).array(),
  files: z.lazy(() => ApplicationFileWithRelationsSchema).array(),
  openApis: z.lazy(() => OpenApiWithRelationsSchema).array(),
  applicationScoping: z.lazy(() => ApplicationScopingWithRelationsSchema).array(),
  scopingFromRepo: z.lazy(() => ScopingFromRepoWithRelationsSchema).array(),
  aiComponents: z.lazy(() => AiComponentWithRelationsSchema).array(),
  aiComponentRelations: z.lazy(() => AiComponentRelationWithRelationsSchema).array(),
  adaptiveScanningBlockObservations: z.lazy(() => AdaptiveScanningBlockObservationWithRelationsSchema).array(),
  generationLimits: z.lazy(() => ApplicationGenerationLimitWithRelationsSchema).array(),
  fullRescanRuns: z.lazy(() => FullRescanRunWithRelationsSchema).array(),
}))